import { decayedLevel } from "@bindery/shared/contracts/decay";
import {
  getWordReadingFluencyLevel,
  WordReadingFluencyEnum,
  type ResponseId,
} from "@bindery/shared/contracts/fluency";
import { extractNormalizedWord } from "@bindery/shared/contracts/word";
import type { FluencyPredictionOutcome } from "@bindery/shared/reader/fluency-prediction";
import type { CompositionToken, SightStatus, SightWord } from "./schema";

/** A reader's stored evidence for one word, as `fluencyRecords` keeps it. */
export interface FluencyEvidence {
  readonly responseHistory: readonly ResponseId[];
  readonly lastUpdated: number;
}

export interface WordPrediction {
  readonly pKnown: number;
  readonly predictedKnown: boolean;
  readonly modelRelease: string;
}

// Emerging is Initial or Developing (plan § 3). A record decayed to Unknown
// places the word nowhere, so it says no more than having no record.
const statusOfLevel = (level: WordReadingFluencyEnum): SightStatus | null => {
  switch (level) {
    case WordReadingFluencyEnum.Strong:
    case WordReadingFluencyEnum.Learned:
      return "sight";
    case WordReadingFluencyEnum.Developing:
    case WordReadingFluencyEnum.Initial:
      return "emerging";
    default:
      return null;
  }
};

/**
 * One word's place in a reader's sight vocabulary at `now`. Evidence that
 * places the word wins over prediction, so a word is either observed or
 * predicted, never a blend, and the result says which. `level` reports the
 * decayed record whenever there is one, even one that no longer places it.
 */
export function sightWord(
  word: string,
  record: FluencyEvidence | null,
  prediction: WordPrediction | null,
  now: number,
): SightWord {
  const key = extractNormalizedWord(word);
  const level =
    record !== null && record.responseHistory.length > 0
      ? decayedLevel({
          level: getWordReadingFluencyLevel([...record.responseHistory]),
          updatedAt: record.lastUpdated,
          now,
        })
      : null;
  const observed = level === null ? null : statusOfLevel(level);
  if (observed !== null) {
    return {
      word: key,
      status: observed,
      level,
      pKnown: null,
      modelRelease: null,
      evaluatedAt: now,
    };
  }
  if (prediction !== null) {
    return {
      word: key,
      status: prediction.predictedKnown
        ? "predicted-sight"
        : "predicted-unknown",
      level,
      pKnown: prediction.pKnown,
      modelRelease: prediction.modelRelease,
      evaluatedAt: now,
    };
  }
  return {
    word: key,
    status: "unobserved",
    level,
    pKnown: null,
    modelRelease: null,
    evaluatedAt: now,
  };
}

/** A predictor outcome as per-word predictions; empty when the model was not run. */
export function predictionIndex(
  outcome: FluencyPredictionOutcome,
): ReadonlyMap<string, WordPrediction> {
  if (outcome.kind !== "predicted") return new Map();
  const modelRelease = `fluency-model v${outcome.modelManifest.version} ${outcome.modelManifest.sha256}`;
  return new Map(
    outcome.predictions.map((p) => [
      extractNormalizedWord(p.word),
      { pKnown: p.pKnown, predictedKnown: p.predictedKnown, modelRelease },
    ]),
  );
}

/**
 * A reader's sight-vocabulary map at one instant: the system-facing lookup
 * text selection, aid rendering, surfacing and keyboard bias read. A word
 * absent from `words` is `unobserved`; `sightOf` answers for it.
 */
export interface SightMap {
  readonly evaluatedAt: number;
  readonly words: ReadonlyMap<string, SightWord>;
}

export function sightMap(input: {
  readonly records: readonly (FluencyEvidence & { readonly word: string })[];
  readonly predictions: ReadonlyMap<string, WordPrediction>;
  readonly now: number;
}): SightMap {
  const records = new Map(
    input.records.map((r) => [extractNormalizedWord(r.word), r]),
  );
  const keys = new Set([...records.keys(), ...input.predictions.keys()]);
  return {
    evaluatedAt: input.now,
    words: new Map(
      [...keys].map((key) => [
        key,
        sightWord(
          key,
          records.get(key) ?? null,
          input.predictions.get(key) ?? null,
          input.now,
        ),
      ]),
    ),
  };
}

/** A word in any surface form, looked up in the map's keyspace. */
export function sightOf(map: SightMap, word: string): SightWord {
  return (
    map.words.get(extractNormalizedWord(word)) ??
    sightWord(word, null, null, map.evaluatedAt)
  );
}

export interface TokenSight {
  readonly tokenId: string;
  readonly sight: SightWord;
}

export interface Coverage {
  /** Running words with text; unresolved slots are counted apart. */
  readonly words: number;
  readonly unresolved: number;
  readonly byStatus: Readonly<Record<SightStatus, number>>;
  /** Share of running words observed as sight; null for a text with none. */
  readonly sightShare: number | null;
  /** The same, counting words predicted to be sight. */
  readonly sightOrPredictedShare: number | null;
}

export interface Overlay {
  readonly tokens: readonly TokenSight[];
  readonly coverage: Coverage;
}

const NO_STATUSES: Record<SightStatus, number> = {
  sight: 0,
  emerging: 0,
  "predicted-sight": 0,
  "predicted-unknown": 0,
  unobserved: 0,
};

/** Lays any text's tokens on the map: each word's place, and the text's coverage. */
export function overlay(
  tokens: readonly CompositionToken[],
  map: SightMap,
): Overlay {
  const sights = tokens.flatMap((t) =>
    t.kind === "word" ? [{ tokenId: t.id, sight: sightOf(map, t.text) }] : [],
  );
  const byStatus = sights.reduce(
    (acc, { sight }) => ({ ...acc, [sight.status]: acc[sight.status] + 1 }),
    NO_STATUSES,
  );
  const words = sights.length;
  const share = (n: number) => (words === 0 ? null : n / words);
  return {
    tokens: sights,
    coverage: {
      words,
      unresolved: tokens.filter((t) => t.kind === "unresolved").length,
      byStatus,
      sightShare: share(byStatus.sight),
      sightOrPredictedShare: share(
        byStatus.sight + byStatus["predicted-sight"],
      ),
    },
  };
}
